<script lang="ts">
  import { page } from "$app/state";
  import { goto } from "$app/navigation";
  import { onMount } from "svelte";
  import Page from "$lib/Page.svelte";
  import Fuse from "fuse.js";
  import ListArticles from "$lib/ListArticles.svelte";
  import Breadcrumb from "$lib/Breadcrumb.svelte";
  import type { ArticleCard } from "$lib/types/shared";
  import { SvelteURLSearchParams } from "svelte/reactivity";

  const pageData = {
    title: "Søk på ishavskraft.no",
    description: "Søk etter innhold på ishavskraft.no.",
  };
  const timeout = 325;
  const key = "term";
  const urlFriendly = (str: string) => encodeURIComponent(str.toLowerCase().replace(/ /g, "-"));

  const queSearch = () => {
    clearTimeout(quedSearch);
    quedSearch = setTimeout(() => {
      const forString = searchString.trim();
      if (!fuseArticles) setFuse(forString);
      else getResults(forString);
    }, timeout);
  };

  const setFuse = async (forString = "") => {
    const fetchRes = await fetch("/api/privat/artikler", {
      cache: "no-cache",
    });
    const data = await fetchRes.json();

    fuseArticles = data?.articles
      ? new Fuse(data.articles, {
          includeScore: true,
          keys: ["title", "lead"],
          useExtendedSearch: true, // mostly for AND operator
          ignoreLocation: true, // it won't matter where in the string the pattern appears
          threshold: 0.2, // a threshold of 0.0 requires a perfect match (of both letters and location), a threshold of 1.0 would match anything
        })
      : null;

    getResults(forString);
  };

  const getResults = (forString = "") => {
    const { pathname } = page.url;
    const searchParams = new SvelteURLSearchParams(window.location.search);

    if (!fuseArticles) return;

    if (forString) searchParams.set(key, urlFriendly(forString));
    else searchParams.delete(key);

    goto(`${pathname}?${searchParams}`, {
      replaceState: true,
      noScroll: true,
      keepFocus: true,
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const searchResult: any[] = fuseArticles.search(forString);
    if (!searchResult) return;
    resArticles = searchResult;
  };

  let quedSearch: ReturnType<typeof setTimeout>;
  let searchString = $state("");
  let fuseArticles: {
    search: (forString: string) => unknown[];
  } | null = null;
  let resArticles:
    | {
        item: ArticleCard;
      }[]
    | null = $state(null);

  onMount(() => {
    let initialSearchTerm: string | null = searchString;

    // start init search if URL contains query
    if (!initialSearchTerm) {
      // get URL search query "term"
      initialSearchTerm = new URLSearchParams(window.location.search).get(key);
    }

    if (initialSearchTerm) {
      searchString = decodeURIComponent(initialSearchTerm.replace(/-/g, " "));
      queSearch();
    }
  });
</script>

<Breadcrumb
  breadcrumbs={!searchString
    ? [
        { href: "/", label: "Forside" },
        { href: "/sok", label: "Søk" },
      ]
    : [
        { href: "/", label: "Forside" },
        { href: "/sok", label: "Søk" },
        { href: "", label: `${searchString}` },
      ]}
/>

<Page class="min-h-[100lvh]" data={pageData} contentNestingLevel={null} descriptionAsLead={false}>
  <form
    action="#"
    name="search"
    role="search"
    class="mx-edge-x grid max-w-prose gap-2 grid-cols-[auto_1fr] items-center"
  >
    <label class="font-600" for="search-input"> Søk </label>
    <input
      class="text-input"
      name="search-input"
      id="search-input"
      type="search"
      placeholder="Søk"
      onkeydown={queSearch}
      bind:value={searchString}
    />
  </form>

  {#if searchString}
    <h2 class="mx-edge-x my-10">
      Søkeresultater for «{searchString}»
    </h2>
  {/if}

  <div aria-live="polite">
    {#if resArticles?.length}
      {@const articles = resArticles.map((res) => res.item)}
      <ListArticles
        class="children-my-10"
        listTitle="I artikler"
        listTitleLevel={3}
        {articles}
        displayFields={{ date: true }}
      />
    {/if}
  </div>
</Page>
